import { Calendar, Card, Select, Row, Col, Button, Empty } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { connect, mapProps } from "@formily/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import type { AppointmentTimeSlotsForPlan } from "@rumble-it/common-lib/types/models/AppointmentTimeSlotsForPlan.interface.ts";

dayjs.locale("nl");

interface RumbleCalendarProps {
  servicePartnerIri: string;
  dealershipCompanyIri?: string;
  insuranceCompanyIri?: string;
  leaseCompanyIri?: string;
  queryFn?: (
    id: string,
    companyId: string | null,
    dealershipId: string | null,
  ) => Promise<AppointmentTimeSlotsForPlan[]>;
  value: string | null;
  onChange: (date: dayjs.Dayjs | null) => void;
}

export const RumbleCalendarBase = (props: RumbleCalendarProps) => {
  const servicePartnerId = props.servicePartnerIri
    ? props.servicePartnerIri.split("/").pop()
    : null;

  const dealershipId = props.dealershipCompanyIri
    ? (props.dealershipCompanyIri.split("/").pop() ?? null)
    : null;

  const leaseCompanyId = props.leaseCompanyIri
    ? props.leaseCompanyIri.split("/").pop()
    : null;

  const insuranceCompanyId = props.insuranceCompanyIri
    ? props.insuranceCompanyIri.split("/").pop()
    : null;

  const companyId = leaseCompanyId ?? insuranceCompanyId ?? null;

  const appointmentTimeSlotsQuery = useQuery({
    queryKey: [
      "appointmentTimeSlots",
      servicePartnerId,
      companyId,
      dealershipId,
    ],
    queryFn: () => {
      if (!props.queryFn) throw new Error("queryFn is not provided");
      if (!servicePartnerId)
        throw new Error("servicePartnerId is not provided");
      return props.queryFn(servicePartnerId, companyId, dealershipId);
    },
    enabled: !!props.queryFn && !!servicePartnerId,
  });

  if (appointmentTimeSlotsQuery.isLoading) {
    return <Card loading={true} />;
  }
  if (!servicePartnerId) {
    return (
      <Card>
        <Empty description="Kies een hersteller om de beschikbaarheid te zien" />
      </Card>
    );
  }

  if (appointmentTimeSlotsQuery.isError) {
    return (
      <Card title="Error" extra={appointmentTimeSlotsQuery.error.message} />
    );
  }

  if (!appointmentTimeSlotsQuery.data) {
    // Explicit empty state instead of returning null
    return (
      <Card>
        <Empty description="Geen beschikbare data gevonden" />
      </Card>
    );
  }

  // Normalize timeslot data once
  const timeSlots = appointmentTimeSlotsQuery.data?.[0]?.items ?? [];
  const availableDates: string[] = timeSlots.map((i) => i.date);
  const selectedValue = props.value ? dayjs(props.value) : null;

  // Helper to test if a date is selectable
  const isSelectableDate = (d: dayjs.Dayjs) =>
    availableDates.includes(d.format("YYYY-MM-DD"));

  // Render a single date cell
  const renderDateCell = (current: dayjs.Dayjs, info: any) => {
    if (info.type !== "date") return info.originNode;

    const baseMonth = props.value ? dayjs(props.value) : dayjs();
    const inView =
      current.month() === baseMonth.month() &&
      current.year() === baseMonth.year();
    const selectable = isSelectableDate(current);

    if (selectable) {
      const isSelected = selectedValue
        ? current.isSame(selectedValue, "day")
        : false;
      const chipClass = isSelected
        ? "bg-black text-white"
        : "bg-[#f2f2f2] text-black";
      return (
        <div className="h-full flex items-center justify-center">
          <div
            className={`rounded-full w-7 h-7 flex items-center justify-center text-xs ${chipClass} ${
              inView ? "" : "opacity-50"
            }`}
          >
            {current.date()}
          </div>
        </div>
      );
    }

    if (!inView) {
      return <div className="opacity-50">{info.originNode}</div>;
    }
    return info.originNode;
  };

  // Custom header renderer
  const renderHeader = ({
    value,
    onChange,
  }: {
    value: dayjs.Dayjs;
    onChange: (d: dayjs.Dayjs) => void;
  }) => {
    const currentYear = dayjs().year();
    const years = [currentYear, currentYear + 1]; // TODO: make configurable via props if needed

    const months = Array.from({ length: 12 }, (_, i) => ({
      label: dayjs().month(i).format("MMMM"),
      value: i,
    }));

    const onYearChange = (year: number) => {
      const newVal = value?.clone?.() ?? dayjs();
      onChange(newVal.year(year));
    };

    const onMonthChange = (month: number) => {
      const newVal = value?.clone?.() ?? dayjs();
      onChange(newVal.month(month));
    };

    const baseVal = value?.clone?.() ?? dayjs();
    const prevVal = baseVal.subtract(1, "month");
    const nextVal = baseVal.add(1, "month");
    const prevDisabled = !years.includes(prevVal.year());
    const nextDisabled = !years.includes(nextVal.year());

    const goPrev = () => {
      if (!prevDisabled) onChange(prevVal);
    };
    const goNext = () => {
      if (!nextDisabled) onChange(nextVal);
    };

    return (
      <Row className="p-3" gutter={16} align="middle">
        <Col flex={"120px"}>
          <Row gutter={8} align="middle">
            <Col span={12}>
              <Button
                block
                icon={<LeftOutlined />}
                onClick={goPrev}
                disabled={prevDisabled}
              />
            </Col>
            <Col span={12}>
              <Button
                block
                icon={<RightOutlined />}
                onClick={goNext}
                disabled={nextDisabled}
              />
            </Col>
          </Row>
        </Col>
        <Col flex="auto">
          <Select
            size="middle"
            className="w-full"
            options={months}
            value={value.month()}
            onChange={onMonthChange}
          />
        </Col>
        <Col flex="auto">
          <Select
            size="middle"
            className="w-full"
            options={years.map((y) => ({ label: String(y), value: y }))}
            value={value.year()}
            onChange={onYearChange}
          />
        </Col>
      </Row>
    );
  };

  return (
    <Calendar
      fullCellRender={renderDateCell}
      headerRender={renderHeader}
      disabledDate={(date) => !isSelectableDate(date)}
      fullscreen={false}
      onChange={props.onChange}
      value={props.value ? dayjs(props.value) : undefined}
    />
  );
};

export const RumbleCalendar = connect(
  RumbleCalendarBase,
  mapProps((props) => ({
    ...props,
    queryFn: props.queryFn,
    servicePartnerIri: props?.servicePartnerIri,
    dealershipCompanyIri: props?.dealershipCompanyIri,
    insuranceCompanyIri: props?.insuranceCompanyIri,
    leaseCompanyIri: props?.leaseCompanyIri,
  })),
);

export default RumbleCalendar;
